/**
 * What `PATCH /personaltrainer/{id}` is sent when the trainer presses „Zapisz zmiany".
 *
 * ── The sign-in address is never part of it, and that is a backend rule ──────────────────────
 * `PatchPersonalTrainerRequest` carries an `email` field, and the endpoint refuses it:
 * `train-with-me-app/docs/adr/2026-08-26-the-sign-in-address-is-not-self-editable.md`, answered as
 * `email_change_unsupported`. The address is Supabase's identity, not a profile column, and nothing
 * on this side may pretend otherwise. So the field is not merely left out of the form — it is
 * unreachable from this function's input, which is why the test that pins it can be written at all.
 *
 * ── An unchanged field is not sent ───────────────────────────────────────────────────────────
 * A PATCH naming every field is a PUT wearing a smaller verb: it would overwrite a value another
 * client changed a second ago with the copy this browser happens to be holding. So a field travels
 * only when the trimmed value actually differs from what was read.
 *
 * ── An emptied field is not sent either ──────────────────────────────────────────────────────
 * `name` and `surname` are the two things a client sees. Clearing one and saving would leave a
 * trainer nameless on every client's phone, and the form has no way to say that was deliberate —
 * so an empty value is treated as "not changed" rather than as "make it empty". A trainer who
 * genuinely wants a blank name is a case nobody has asked for, and inventing a path for it here
 * would ship the accidental one.
 *
 * ── The draft holds only what the trainer EDITED ─────────────────────────────────────────────
 * A field nobody touched follows the read. Holding both names the moment one was typed froze the
 * other at that instant: a surname changed on the phone and refetched here was then sent back
 * stale by „Zapisz", reverting the phone's edit, and the untouched field counted as unsaved work.
 *
 * Pure: two objects in, one object out. No React, no DOM, no i18next.
 */

import type { TrainWithMeModulesClientsDtosRequestsPatchPersonalTrainerRequest as PatchTrainerRequest } from '~/shared/api/generated/model';

/** The fields the trainer has edited, and only those. The address is deliberately absent — see the header. */
export interface ProfileDraft {
  readonly firstName?: string;
  readonly lastName?: string;
}

/** What `GET /personaltrainer/{id}` said, reduced to the two fields this form can move. */
export interface ProfileOrigin {
  readonly firstName: string;
  readonly lastName: string;
}

/** What the two fields show: each edited value over what the read says now. */
export function profileForm(draft: ProfileDraft, origin: ProfileOrigin): ProfileOrigin {
  return {
    firstName: draft.firstName ?? origin.firstName,
    lastName: draft.lastName ?? origin.lastName,
  };
}

/**
 * The patch body, or `null` when there is nothing to send.
 *
 * `null` rather than `{}` so the caller cannot accidentally issue an empty request: a save with an
 * empty patch is not a save, and the button that raises it is refused before it gets here.
 */
export function profilePatch(draft: ProfileDraft, origin: ProfileOrigin): PatchTrainerRequest | null {
  const patch: PatchTrainerRequest = {};

  const { firstName, lastName } = profileForm(draft, origin);

  const name = firstName.trim();
  const surname = lastName.trim();
  if (name !== '' && name !== origin.firstName.trim()) patch.name = name;
  if (surname !== '' && surname !== origin.lastName.trim()) patch.surname = surname;

  return Object.keys(patch).length === 0 ? null : patch;
}

/**
 * Whether the save button is offered. The same rule the patch is built from, so the control and
 * the request cannot disagree about what "changed" means.
 */
export function profileSaveIsOffered(draft: ProfileDraft, origin: ProfileOrigin): boolean {
  return profilePatch(draft, origin) !== null;
}
